import {
  Button,
  Col,
  Divider,
  Form,
  Popover,
  Row,
  Skeleton,
  notification,
} from 'antd';

import { PageProps } from '@/types/PageProps';

import { DefaultRouteTabs, Group, SaveButton } from '@/components/_common';

import { useReleasePurchaseFormContext } from '../../ReleasesPurchaseProvider';
import { useEffect, useState } from 'react';
import { MathRound } from '@/utils/functions/MathRound';
import moment, { Moment } from 'moment';
import InstallmentFormFields from '@/components/Movements/Purchases/PurchaseDetails/PurchaseFinancial/InstallmentFormFields';
import PurchaseFinancialInstallments from '@/components/Movements/Purchases/PurchaseDetails/PurchaseFinancial/PurchaseFinancialInstallments';
import { GenerateFinancialByPurchase } from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import { DateTimeFormat } from '@/utils/moment';
import {
  useGenerateFinancialByPurchase,
  useListAccountPayableByPurchaseId,
} from '@/core/domain/financial/AccountPayable/AccountPayable.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import InstallmentByPurchaseList from '@/components/Movements/Purchases/PurchaseDetails/PurchaseFinancial/InstallmentByPurchaseList';
import { Status } from '@/utils/enum/statusEnum';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import ButtonBack from '@/components/_common/ButtonBack';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import purchaseValidationTabs from '@/utils/functions/purchase/purchaseValidationTabs';
import { usePurchaseXml } from '@/core/domain/purchase/Purchase.hooks';
import { queryClient } from '@/Root';
import { purchaseXmlInterface } from '@/core/domain/purchase/Purchase.types';
import SituationFormField from '@/components/Movements/DirectSale/SituationFormField';
import PurcheseAlertBoshMigration from '@/components/Movements/Purchases/PurchaseAlertBoshMigration';
import { Info } from 'react-feather';

export interface PurchaseFinancialFormValues {
  situation: Status;
  planAccountId: number;
  costCenterId: number;
  installmentsFirstDueDate: Moment;
  quantityInstallments: number;
  intervalDays: number;
  totalAmount: number;
}

export interface PurchaseFinancialInstallmentList {
  description: string;
  documentNumber?: string;
  documentTypeId?: number;
  bankAccountId?: number;
  installmentValue: number;
  dueDate: Moment;
}

function PurchaseFinancialPage({ route }: PageProps) {
  const { purchaseId, instanceForms, disabled, purchase, situation } =
    useReleasePurchaseFormContext();
  const [form] = Form.useForm<PurchaseFinancialFormValues>(
    instanceForms.purchaseFinancialForm,
  );
  const [installments, setInstallments] = useState<
    PurchaseFinancialInstallmentList[]
  >([]);

  const firstDueDate = Form.useWatch('installmentsFirstDueDate', form);
  const daysInterval = Form.useWatch('intervalDays', form);
  const installmentQuantity = Form.useWatch('quantityInstallments', form);
  const totalAmountInstallments = installments.reduce(
    (acc, curr) => (acc += curr.installmentValue ?? 0),
    0,
  );

  const {
    data: financialList,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = useListAccountPayableByPurchaseId({
    purchaseId: Number(purchaseId),
    order: 'DESC',
  });

  const { mutateAsync: mutateXml, isLoading: xmlLoading } = usePurchaseXml();

  const xmlData = queryClient.getQueryData([
    'purchaseXml',
    { purchaseId: Number(purchaseId) },
  ]) as purchaseXmlInterface[];

  const installmentsByPurchase = flatInfiniteData(financialList);

  const generateFinancialMutation = useGenerateFinancialByPurchase();

  useEffect(() => {
    if (purchase) {
      form.setFieldsValue({
        ...purchase,
        installmentsFirstDueDate: moment(),
        intervalDays: 30,
        quantityInstallments: 1,
        planAccountId: purchase?.supplierDTO.supplierPlanAccountId,
      });
    }
  }, [purchase]);

  useEffect(() => {
    if (
      daysInterval &&
      firstDueDate &&
      installmentQuantity &&
      purchase?.totalAmount
    ) {
      const installmentValue = MathRound(
        purchase?.totalAmount / installmentQuantity,
        2,
      );

      setInstallments(
        new Array(installmentQuantity)
          .fill(0)
          .reduce<PurchaseFinancialInstallmentList[]>((acc, _, i, a) => {
            const dueDate =
              i === 0
                ? firstDueDate?.clone()
                : acc.at(-1)?.dueDate.clone().add(daysInterval, 'days');

            let installmentValueItem: number;

            if (i === installmentQuantity - 1) {
              installmentValueItem = MathRound(
                purchase?.totalAmount -
                  installmentValue * installmentQuantity +
                  installmentValue,
                2,
              );
            } else {
              installmentValueItem = installmentValue;
            }

            return [
              ...acc,
              {
                description: `Compra ${purchase.code}/${i + 1} ${
                  !purchase.documentNumber ? '' : `- ${purchase.documentNumber}`
                }`,
                documentNumber:
                  xmlData?.length === a.length && xmlData[i]
                    ? `${xmlData[i]?.billNumber} - ${xmlData[i]?.duplicateNumber}`
                    : '',
                dueDate:
                  xmlData?.length === a.length &&
                  xmlData[i] &&
                  !!xmlData[i]?.dueDate
                    ? moment(xmlData[i].dueDate)
                    : dueDate ?? moment(),
                installmentValue:
                  xmlData?.length === a.length
                    ? xmlData[i]?.duplicateValue || 0
                    : installmentValueItem,
              },
            ];
          }, [] as PurchaseFinancialInstallmentList[]),
      );
    }
  }, [purchase, firstDueDate, daysInterval, installmentQuantity]);

  const handleGenerateFinancial = async () => {
    const values = await form.validateFields();

    if (purchase && installments) {
      if (installments.every((installment) => !installment.documentTypeId)) {
        return notification.error({
          message: 'Informe o(s) tipo(s) de documento',
        });
      } else if (
        installments.every((installment) => !installment.bankAccountId)
      ) {
        return notification.error({
          message: 'Informe a(s) conta(s) bancária(s)',
        });
      }

      if (
        totalAmountInstallments.toFixed(2) !== purchase?.totalAmount.toFixed(2)
      ) {
        return notification.error({
          message: 'Aviso!',
          description:
            'O somatório do valor das parcelas não pode ser diferente do valor total da compra.',
        });
      }

      const obj: GenerateFinancialByPurchase = {
        ...values,
        purchaseId: purchase.id,
        companyId: purchase.companyId,
        supplierId: purchase.supplierDTO.id,
        installmentPayablePurchases: installments.map((item) => ({
          ...item,
          dueDate: item.dueDate.format(DateTimeFormat),
        })),
      };

      await generateFinancialMutation.mutateAsync(obj);
    }
  };

  const handleFinancialXml = () => {
    if (typeof xmlData === 'undefined') {
      mutateXml({ purchaseId: Number(purchaseId) }).then((response) => {
        form.setFieldsValue({
          quantityInstallments: response?.length || 1,
          installmentsFirstDueDate:
            !!response?.at(0) && !!response[0]?.dueDate
              ? moment(response[0].dueDate)
              : moment(),
        });
      });
    } else {
      form.setFieldsValue({
        quantityInstallments: xmlData?.length || 1,
        installmentsFirstDueDate:
          !!xmlData?.at(0) && !!xmlData[0]?.dueDate
            ? moment(xmlData[0].dueDate)
            : moment(),
      });
    }
  };

  return (
    <DefaultRouteTabs
      permissions={route.permissions}
      route={purchaseValidationTabs(route, purchase)}
      baseKey={purchaseId}
      basePath={`/app/movements/purchases/purchasesReleases/${purchaseId}`}
      extra={
        <>
          <SaveButton
            type="primary"
            block
            loading={generateFinancialMutation.isLoading}
            onClick={handleGenerateFinancial}
            disabled={
              isLoading ||
              !!installmentsByPurchase.length ||
              !hasPermissions([
                Authorities.MOVIMENTATION_PURCHASE_GENERATE_FINANCIAL_PURCHASE,
              ]) ||
              purchase?.boschMigration
            }
          >
            Gerar financeiro
          </SaveButton>
        </>
      }
    >
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <SituationFormField initialValue={situation} />
        </Col>

        {purchase?.boschMigration && (
          <Col span={24}>
            <PurcheseAlertBoshMigration />
          </Col>
        )}

        <Col span={24}>
          <Skeleton loading={isLoading}>
            {!installmentsByPurchase.length ? (
              <Row gutter={[12, 12]}>
                <Col span={24}>
                  <InstallmentFormFields form={form} />
                </Col>

                {purchase?.hasPurchaseBillingXml && (
                  <Col span={24}>
                    <Group gap={5} position="right">
                      <Button
                        type="primary"
                        onClick={handleFinancialXml}
                        disabled={xmlLoading || xmlData?.length === 0}
                      >
                        Parcelamento importado
                      </Button>

                      <Popover
                        title="Importar parcelamento XML"
                        placement="left"
                        content={
                          <div style={{ maxWidth: 400 }}>
                            <p>
                              Esta função serve para importar automaticamente as
                              condições de pagamento da nota fiscal enviada pelo
                              fornecedor.
                            </p>
                            <p>
                              Assim, o sistema preenche as parcelas da compra
                              conforme foram definidas pelo fornecedor, sem que
                              você precise digitar manualmente.
                            </p>
                            <p>
                              Isso garante que o pagamento siga as mesmas regras
                              acordadas na nota fiscal.
                            </p>
                          </div>
                        }
                      >
                        <Info size={20} />
                      </Popover>
                    </Group>
                  </Col>
                )}

                <Col span={24}>
                  <PurchaseFinancialInstallments
                    installments={installments}
                    setInstallments={setInstallments}
                  />
                </Col>
              </Row>
            ) : (
              <InstallmentByPurchaseList
                installmentsByPurchase={installmentsByPurchase}
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                isFetching={isLoading}
              />
            )}
          </Skeleton>
        </Col>

        <Divider />

        <SpaceForButton>
          <ButtonBack
            path="/app/movements/purchases/purchasesReleases"
            disabled={disabled}
          />
        </SpaceForButton>
      </Row>
    </DefaultRouteTabs>
  );
}

export default PurchaseFinancialPage;
